import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import MainLayout from './components/MainLayout';
import FileUpload from './Pages/fileUpload'; // Adjusted import path
import InitialQuestion from './Pages/InitialQuestion';
import DescribeIssueParent from './Pages/DescribeIssueParent';
import ProfilePage from './Pages/ProfilePage';
import LoginPage from './Pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute'; // Import the ProtectedRoute component
import UserHomePage from './Pages/UserHomePage';
import AuthTokenProvider from './components/AuthTokenProvider'; // Import the AuthTokenProvider
import WelcomePage from './Pages/WelcomePage';
import SubscriptionNeededPage from './Pages/SubscriptionNeededPage';
import SubscriptionSuccessfulPage from './Pages/SubscriptionSuccessfulPage';
import InspectionEvaluation from './Pages/InspectionEvaluation';
import ImprovementEvaluation from './Pages/ImprovementEvaluation';
import InspectionEvaluationHome from './Pages/InspectionEvaluationHome';
import ImprovementEvaluationHome from './Pages/ImprovementEvaluationHome';
import PropertyViewPage from './Pages/PropertyViewPage';
import PropertyListPage from './Pages/PropertyListPage';
import QuoteAnalysis from './Pages/QuoteAnalysis';
import { SOWListPage } from './Pages/SOW/SOWListPage';
import { SOWGenerationPage } from './Pages/SOW/SOWGenerationPage';
import { SOWOverviewPage } from './Pages/SOW/SOWOverviewPage';
import { SOWChecklistPage } from './Pages/SOW/SOWChecklistPage';
import { WorkItemRefinementPage } from './Pages/SOW/WorkItemRefinementPage';
import { QuestionnaireRefinementPage } from './Pages/SOW/QuestionnaireRefinementPage';
import { BidsPage } from './Pages/BidsPage';
import { BidDetailPage } from './Pages/BidDetailPage';
import { AdminPage } from './Pages/AdminPage';
import AdminRoute from './components/AdminRoute';
import { PublicLandingPage } from './Pages/PublicLandingPage';
import { LoadScript } from '@react-google-maps/api';


const theme = createTheme({
  palette: {
    primary: {
      main: '#131E3A',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#fafafa',
    },
    text: {
      primary: '#212121',
    },
  },
});

const App: React.FC = () => {
  return (
    <Auth0Provider
    domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
    clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE || ''
    }}
  >
      <AuthTokenProvider>
      <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
      libraries={['places']} // Ensure the Places library is loaded
    >
        <ThemeProvider theme={theme}>
          <Router>
            <Routes>
              {/* Public routes (no auth required, no MainLayout) */}
              <Route path="/scope/:alias/new" element={<PublicLandingPage />} />
              <Route path="/scope/:sowId/wizard" element={<SOWGenerationPage />} />
              <Route path="/scope/:sowId" element={<SOWOverviewPage />} />
              <Route path="/scope/:sowId/questionnaire" element={<SOWChecklistPage />} />

              <Route path="/*" element={
                <>
                  <MainLayout />
                  <div className="pt-16 pl-0 md:pl-64 min-h-screen">
                    <Routes>
                      <Route path="/" element={<ProtectedRoute component={PropertyListPage} />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/welcome" element={<ProtectedRoute component={WelcomePage} />} />
                      <Route path="/upload" element={<ProtectedRoute component={FileUpload} />} />
                      <Route path="/repair" element={<ProtectedRoute component={DescribeIssueParent} />} />
                      <Route path="/repair/:id" element={<ProtectedRoute component={DescribeIssueParent} />} />
                      <Route path="/project" element={<ProtectedRoute component={FileUpload} />} />
                      <Route path="/profile" element={<ProtectedRoute component={ProfilePage} />} />
                      <Route path="/subscription" element={<ProtectedRoute component={SubscriptionNeededPage} />} />
                      <Route path="/success" element={<ProtectedRoute component={SubscriptionSuccessfulPage} />} />
                      <Route path="/inspection" element={<ProtectedRoute component={InspectionEvaluationHome} />} />
                      <Route path="/inspection/:inspectionId" element={<ProtectedRoute component={InspectionEvaluation} />} />
                      <Route path="/improvement" element={<ProtectedRoute component={ImprovementEvaluation} />} />
                      <Route path="/property/:propertyId/*" element={<ProtectedRoute component={PropertyViewPage} />} />
                      <Route path="/property/" element={<ProtectedRoute component={PropertyListPage} />} />
                      <Route path="/quote/" element={<ProtectedRoute component={QuoteAnalysis} />} />
                      <Route path="/sow/" element={<ProtectedRoute component={SOWListPage} />} />
                      <Route path="/sow/:sowId" element={<ProtectedRoute component={SOWOverviewPage} />} />
                      <Route path="/sow/:sowId/questionnaire" element={<ProtectedRoute component={SOWChecklistPage} />} />
                      <Route path="/sow/:sowId/questionnaire/:questionnaireId" element={<ProtectedRoute component={QuestionnaireRefinementPage} />} />
                      <Route path="/sow/:sowId/workitem/:workItemId/refinement/:conversationId?" element={<ProtectedRoute component={WorkItemRefinementPage} />} />
                      <Route path="/sow/:sowId/wizard" element={<ProtectedRoute component={SOWGenerationPage} />} />
                      <Route path="/bids/" element={<ProtectedRoute component={BidsPage} />} />
                      <Route path="/bids/:bidId" element={<ProtectedRoute component={BidDetailPage} />} />
                      <Route path="/admin" element={<AdminRoute component={AdminPage} />} />
                    </Routes>
                  </div>
                </>
              } />
            </Routes>
          </Router>
        </ThemeProvider>
        </LoadScript>
      </AuthTokenProvider>
    </Auth0Provider>
  );
};

export default App;
